import type { PersonalBestEntry } from '@ottd/api';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { BevelWindow } from '@/components/prototype/bevel-window';
import { RelativeTime } from '@/components/prototype/relative-time';
import { TypeTile } from '@/components/prototype/type-tile';
import { cn } from '@/lib/utils';

/*
 * "Personal best times" panel of the player profile: the player's fastest win
 * on each visible server, measured against that server's record. A row that
 * holds (or ties) the record is highlighted and tagged RECORD; the others show
 * the time to beat.
 */
export function PersonalBests({ entries }: { entries: PersonalBestEntry[] }) {
  const { t } = useTranslation('player');

  return (
    <BevelWindow title={t('profile.bestTimes')}>
      {entries.length === 0 ? (
        <p className="text-faint font-mono text-xs">{t('profile.bestTimesEmpty')}</p>
      ) : (
        <ul className="flex flex-col">
          {entries.map((entry) => (
            <PersonalBestRow key={entry.serverId} entry={entry} />
          ))}
        </ul>
      )}
    </BevelWindow>
  );
}

function PersonalBestRow({ entry }: { entry: PersonalBestEntry }) {
  const { t } = useTranslation('player');

  return (
    <li
      className={cn(
        'border-line flex flex-wrap items-center gap-x-3 gap-y-1 border-b px-2 py-1.5 last:border-0',
        entry.isRecord && 'bg-panel-in',
      )}
    >
      <TypeTile type={entry.type} size={22} />
      <span className="text-text min-w-0 flex-1 truncate text-sm">{entry.server}</span>
      <Link
        to={`/game/${entry.gameId}`}
        aria-label={t('profile.gameLabel', { id: entry.gameId })}
        className="text-orange font-mono text-xs"
      >
        #{entry.gameId}
      </Link>
      <RelativeTime
        iso={entry.at}
        label={entry.when}
        className="text-faint font-mono text-[11px]"
      />
      {/* Time + record status wrap together on narrow screens. */}
      <span className="ml-auto flex items-baseline gap-2">
        <span
          className={cn(
            'font-display min-w-[4.5rem] text-right text-xs',
            entry.isRecord ? 'text-gold' : 'text-green',
          )}
        >
          {entry.time}
        </span>
        <RecordStatus entry={entry} />
      </span>
    </li>
  );
}

function RecordStatus({ entry }: { entry: PersonalBestEntry }) {
  const { t } = useTranslation('player');

  if (entry.isRecord) {
    return (
      <span className="font-display text-gold min-w-[6rem] text-right text-[10px]">
        <span aria-hidden="true">{t('profile.record')}</span>
        <span className="sr-only">{t('profile.recordHolder')}</span>
      </span>
    );
  }
  return (
    <span className="text-faint min-w-[6rem] text-right font-mono text-[11px]">
      {t('profile.recordToBeat', { time: entry.serverBestTime ?? '-' })}
    </span>
  );
}
